import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List,
  Save,
  FileText,
  Download,
  FileDown,
  ChevronDown,
  RefreshCw,
  FileSearch,
  ClipboardList
} from 'lucide-react';
import { useTreatmentPlan } from '../contexts/TreatmentPlanContext';
import { apiService } from '../services/api';
import { formatCaseCriteria, hashSessionId } from '../utils/caseCriteriaMapper';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { orthoTrack } from '../utils/posthog';

const DocumentEditor: React.FC = () => {
  const [documentTitle, setDocumentTitle] = useState('Untitled Document');
  const [lastSaved, setLastSaved] = useState<Date>(new Date());
  const [isDirty, setIsDirty] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingCriteria, setIsFetchingCriteria] = useState(false);
  const [isFetchingPlan, setIsFetchingPlan] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLocalEdit = useRef(false);
  
  const { currentTreatmentPlan, treatmentPlans, setCurrentTreatmentPlan, updateTreatmentPlan } = useTreatmentPlan();
  
  // Get current session ID from the API service
  const getCurrentSessionId = () => {
    return apiService.getCurrentSessionId() || 'default';
  };
  
  // Filter treatment plans for current session
  const sessionTreatmentPlans = treatmentPlans.filter(
    plan => plan.sessionId === getCurrentSessionId()
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      BulletList,
      ListItem,
    ],
    editable: true,
    autofocus: false, // Prevent auto-focus issues
    content: currentTreatmentPlan?.content || `
      <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
      <div style="margin-bottom: 20px;">
        <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p><strong>Case ID:</strong> ${getCurrentSessionId() ? hashSessionId(getCurrentSessionId()) : 'Pending'}</p>
      </div>
      <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
      <p style="color: #6b7280; text-align: center;"><em>Treatment plan will be generated after case analysis</em></p>
    `,
    onUpdate: ({ editor }) => {
      setIsDirty(true);
      
      // Don't update the context at all while typing
      // Only save when user explicitly clicks save or after a long delay
    },
  });
  
  // Update editor content when treatment plan changes externally
  useEffect(() => {
    if (currentTreatmentPlan && editor && !isLocalEdit.current) {
      // Only update content if it's a different treatment plan
      if (editor.getHTML() !== currentTreatmentPlan.content) {
        editor.commands.setContent(currentTreatmentPlan.content);
        setDocumentTitle(currentTreatmentPlan.title);
        setIsDirty(false);
      }
    }
  }, [currentTreatmentPlan?.id, editor]); // Only depend on ID change
  
  // Auto-save every 30 seconds if there are unsaved changes
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (isDirty && editor && currentTreatmentPlan) {
        const content = editor.getHTML();
        updateTreatmentPlan(currentTreatmentPlan.id, content);
        setLastSaved(new Date());
        setIsDirty(false);
        console.log('Auto-saved document');
      }
    }, 30000); // 30 seconds
    
    return () => {
      clearInterval(autoSaveInterval);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [isDirty, editor, currentTreatmentPlan, updateTreatmentPlan]);
  
  const handleSave = useCallback(() => {
    if (editor && currentTreatmentPlan) {
      const content = editor.getHTML();
      updateTreatmentPlan(currentTreatmentPlan.id, content);
      console.log('Saving document:', content);
      setLastSaved(new Date());
      setIsDirty(false);
      
      // Track document edit
      const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
      orthoTrack.documentEdited(getCurrentSessionId() || '', wordCount);
    }
  }, [editor, currentTreatmentPlan, updateTreatmentPlan]);

  // Manual fetch case criteria
  const handleFetchCriteria = useCallback(async () => {
    const sessionId = getCurrentSessionId();
    if (!sessionId || sessionId === 'default') {
      console.log('No active session to fetch criteria');
      return;
    }

    setIsFetchingCriteria(true);
    try {
      const caseCriteriaData = await apiService.getCaseCriteria(sessionId);
      
      if (caseCriteriaData && editor) {
        // Get current content
        const currentContent = editor.getHTML();
        
        // Format the criteria section
        const formattedCriteria = formatCaseCriteria(caseCriteriaData);
        let criteriaHtml = `
          <h2>Criteria Analysis</h2>
          <ul style="list-style-type: none; padding-left: 0;">
        `;
        
        const criteriaOrder = [
          'Malocclusion Type',
          'All Permanent Teeth Present',
          'Crowding',
          'Maxillary Transverse Constriction (Premolar Region)',
          'Spee Curve (Lower Arch)',
          'Correction Type Indicated',
          'Upper Midline Alignment',
          'Distalization Amount',
          'Distalization Side'
        ];
        
        criteriaOrder.forEach(field => {
          const value = formattedCriteria[field] || 'Not specified';
          criteriaHtml += `<li style="margin-bottom: 8px;"><strong>${field}:</strong> ${value}</li>`;
        });
        
        criteriaHtml += `</ul>`;
        
        // Update or insert criteria section
        let updatedContent = currentContent;
        if (currentContent.includes('<h2>Criteria Analysis</h2>')) {
          // Replace existing criteria section
          updatedContent = currentContent.replace(
            /<h2>Criteria Analysis<\/h2>[\s\S]*?(?=<h2>|<h3>|$)/,
            criteriaHtml
          );
        } else {
          // Insert after session info
          updatedContent = currentContent.replace(
            /(<p><strong>Session ID:<\/strong>[^<]*<\/p>)/,
            `$1\n${criteriaHtml}`
          );
        }
        
        editor.commands.setContent(updatedContent);
        setIsDirty(true);
        
        console.log('✅ Case criteria fetched and updated');
      }
    } catch (error) {
      console.error('Failed to fetch case criteria:', error);
      alert('Failed to fetch case criteria. Please try again.');
    } finally {
      setIsFetchingCriteria(false);
    }
  }, [editor]);

  // Manual fetch treatment plan
  const handleFetchPlan = useCallback(async () => {
    const sessionId = getCurrentSessionId();
    if (!sessionId || sessionId === 'default') {
      console.log('No active session to fetch plan');
      return;
    }

    setIsFetchingPlan(true);
    try {
      const planData: any = await apiService.getTreatmentPlan(sessionId);
      
      if (planData && editor) {
        // Format the full treatment plan
        const currentDate = new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        const hashedSessionId = hashSessionId(sessionId);
        
        let fullContent = `
          <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
          <div style="margin-bottom: 20px;">
            <p><strong>Date:</strong> ${currentDate}</p>
            <p><strong>Case ID:</strong> ${hashedSessionId}</p>
          </div>
          <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
        `;
        
        // Add treatment plan sections based on available data
        if (planData.treatment_plan) {
          fullContent += `
            <h2>Treatment Plan</h2>
            <div>${planData.treatment_plan.replace(/\n/g, '<br/>')}</div>
          `;
        }
        
        // Check for combined_decisions in treatment_decisions object
        if (planData.treatment_decisions && typeof planData.treatment_decisions === 'object' && 'combined_decisions' in planData.treatment_decisions) {
          const decisions = planData.treatment_decisions.combined_decisions;
          if (Array.isArray(decisions)) {
            fullContent += `
              <h2>Treatment Decisions</h2>
              <ul>
            `;
            decisions.forEach((decision: string) => {
              fullContent += `<li>${decision}</li>`;
            });
            fullContent += `</ul>`;
          }
        } else if (Array.isArray(planData.treatment_decisions)) {
          // Fallback to array format
          fullContent += `
            <h2>Treatment Decisions</h2>
            <ul>
          `;
          planData.treatment_decisions.forEach((decision: string) => {
            fullContent += `<li>${decision}</li>`;
          });
          fullContent += `</ul>`;
        }
        
        // Remove Treatment Phases section - not needed in document
        
        // Add Invisalign plan if available
        if (planData.has_invisalign_plan && planData.invisalign_plan) {
          fullContent += `
            <h2>Invisalign Treatment Plan</h2>
            <div>${planData.invisalign_plan.replace(/\n/g, '<br/>')}</div>
          `;
        }
        
        // Add clinical tips if available
        if (planData.has_clinical_tips && planData.clinical_tips) {
          fullContent += `<h2>Clinical Tips</h2>`;
          
          // Split clinical tips by newlines or bullet points and create numbered list
          const tips = planData.clinical_tips
            .split(/\n+|(?=[-•*]\s)/)  // Split by newlines or bullet points
            .map((tip: string) => tip.replace(/^[-•*]\s*/, '').trim())  // Remove leading bullets/dashes
            .filter((tip: string) => tip.length > 0);  // Remove empty lines
          
          if (tips.length > 0) {
            fullContent += `<ol style="margin-left: 20px;">`;
            tips.forEach((tip: string) => {
              fullContent += `<li style="margin-bottom: 8px;">${tip}</li>`;
            });
            fullContent += `</ol>`;
          } else {
            // Fallback if no proper splitting occurred
            fullContent += `<div>${planData.clinical_tips.replace(/\n/g, '<br/>')}</div>`;
          }
        }
        
        editor.commands.setContent(fullContent);
        setIsDirty(true);
        
        // Update treatment plan in context
        if (currentTreatmentPlan) {
          updateTreatmentPlan(currentTreatmentPlan.id, fullContent);
        }
        
        console.log('✅ Treatment plan fetched and updated');
      }
    } catch (error) {
      console.error('Failed to fetch treatment plan:', error);
      alert('Failed to fetch treatment plan. Please try again.');
    } finally {
      setIsFetchingPlan(false);
    }
  }, [editor, currentTreatmentPlan, updateTreatmentPlan]);

  const handleRefresh = useCallback(async () => {
    const sessionId = getCurrentSessionId();
    if (!sessionId || sessionId === 'default') {
      console.log('No active session to refresh');
      return;
    }

    setIsRefreshing(true);
    try {
      // Fetch latest case criteria and treatment plan
      const [treatmentPlanData, caseCriteriaData] = await Promise.all([
        apiService.getTreatmentPlan(sessionId).catch(err => {
          console.warn('Could not fetch treatment plan:', err);
          return null;
        }),
        apiService.getCaseCriteria(sessionId).catch(err => {
          console.warn('Could not fetch case criteria:', err);
          return null;
        })
      ]);

      if (treatmentPlanData || caseCriteriaData) {
        // Format the updated content
        const currentDate = new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        const hashedSessionId = hashSessionId(sessionId);
        
        let updatedContent = `
          <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
          <div style="margin-bottom: 20px;">
            <p><strong>Date:</strong> ${currentDate}</p>
            <p><strong>Session ID:</strong> ${hashedSessionId}</p>
          </div>
          <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
        `;

        // Add Case Criteria section if available
        if (caseCriteriaData) {
          const formattedCriteria = formatCaseCriteria(caseCriteriaData);
          updatedContent += `
            <h2>Criteria Analysis</h2>
            <ul style="list-style-type: none; padding-left: 0;">
          `;
          
          const criteriaOrder = [
            'Malocclusion Type',
            'All Permanent Teeth Present',
            'Crowding',
            'Maxillary Transverse Constriction (Premolar Region)',
            'Spee Curve (Lower Arch)',
            'Correction Type Indicated',
            'Upper Midline Alignment',
            'Distalization Amount',
            'Distalization Side'
          ];
          
          criteriaOrder.forEach(field => {
            const value = formattedCriteria[field] || 'Not specified';
            updatedContent += `<li style="margin-bottom: 8px;"><strong>${field}:</strong> ${value}</li>`;
          });
          
          updatedContent += `</ul>`;
        }

        // Add treatment plan content if available
        if (treatmentPlanData) {
          if (treatmentPlanData.treatment_plan) {
            updatedContent += `
              <h2>Treatment Plan</h2>
              <div>${treatmentPlanData.treatment_plan.replace(/\n/g, '<br/>')}</div>
            `;
          }

          if (treatmentPlanData.treatment_decisions) {
            updatedContent += `
              <h2>Treatment Decisions</h2>
              <ul>
            `;
            if (Array.isArray(treatmentPlanData.treatment_decisions)) {
              treatmentPlanData.treatment_decisions.forEach((decision: string) => {
                updatedContent += `<li>${decision}</li>`;
              });
            }
            updatedContent += `</ul>`;
          }

          if (treatmentPlanData.treatment_dict_list && treatmentPlanData.treatment_dict_list.length > 0) {
            updatedContent += `
              <h2>Treatment Options</h2>
              <ol>
            `;
            treatmentPlanData.treatment_dict_list.forEach((option: any) => {
              if (typeof option === 'string') {
                updatedContent += `<li>${option}</li>`;
              } else if (option.treatment_option) {
                updatedContent += `<li>${option.treatment_option}</li>`;
              } else if (option.name || option.description) {
                updatedContent += `<li><strong>${option.name || 'Option'}:</strong> ${option.description || ''}</li>`;
              }
            });
            updatedContent += `</ol>`;
          }
          
          // Add clinical tips if available
          const planDataAny = treatmentPlanData as any;
          if (planDataAny.has_clinical_tips && planDataAny.clinical_tips) {
            updatedContent += `<h2>Clinical Tips</h2>`;
            
            let tips: string[] = [];
            if (typeof planDataAny.clinical_tips === 'string') {
              // Split string clinical tips by newlines or bullet points
              tips = planDataAny.clinical_tips
                .split(/\n+|(?=[-•*]\s)/)
                .map((tip: string) => tip.replace(/^[-•*]\s*/, '').trim())
                .filter((tip: string) => tip.length > 0);
            } else if (Array.isArray(planDataAny.clinical_tips)) {
              tips = planDataAny.clinical_tips;
            }
            
            if (tips.length > 0) {
              updatedContent += `<ol style="margin-left: 20px;">`;
              tips.forEach((tip: string) => {
                updatedContent += `<li style="margin-bottom: 8px;">${tip}</li>`;
              });
              updatedContent += `</ol>`;
            }
          }
        }

        // Update the editor content
        if (editor) {
          editor.commands.setContent(updatedContent);
          setIsDirty(false);
          setLastSaved(new Date());
        }

        // Update the treatment plan in context
        if (currentTreatmentPlan) {
          updateTreatmentPlan(currentTreatmentPlan.id, updatedContent);
        }
        
        // Track document refresh
        orthoTrack.documentRefreshed(sessionId);
      }
    } catch (error) {
      console.error('Failed to refresh document:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [editor, currentTreatmentPlan, updateTreatmentPlan]);

  const handleExportHTML = useCallback(() => {
    if (editor) {
      const content = editor.getHTML();
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentTitle}.html`;
      a.click();
      URL.revokeObjectURL(url);
      
      // Track export event
      orthoTrack.treatmentPlanExported('html', getCurrentSessionId());
    }
    setShowExportMenu(false);
  }, [editor, documentTitle]);

  const handleExportPDF = useCallback(async () => {
    if (editor) {
      const editorElement = document.querySelector('.ProseMirror');
      if (editorElement) {
        try {
          // Create a clone of the editor content for PDF generation
          const clonedElement = editorElement.cloneNode(true) as HTMLElement;
          clonedElement.style.width = '800px';
          clonedElement.style.padding = '40px';
          clonedElement.style.backgroundColor = 'white';
          
          // Check if content already has logo/images, if not add them
          const hasLogo = clonedElement.innerHTML.includes('logo.png');
          
          // If images are not in the content, add header with logo
          if (!hasLogo) {
            const headerDiv = document.createElement('div');
            headerDiv.style.textAlign = 'center';
            headerDiv.style.marginBottom = '30px';
            headerDiv.innerHTML = `
              <img src="/logo.png" alt="OrthoMind" style="width: 80px; height: 80px; margin-bottom: 20px;">
              <h1 style="color: #1e293b; font-size: 24px; margin: 0;">OrthoMind</h1>
              <p style="color: #64748b; font-size: 14px; margin-top: 5px;">AI Clinical Decision Support for Orthodontists</p>
            `;
            clonedElement.insertBefore(headerDiv, clonedElement.firstChild);
          }
          
          // Temporarily append to body
          document.body.appendChild(clonedElement);
          
          // Wait for images to load
          const images = clonedElement.getElementsByTagName('img');
          const imagePromises = Array.from(images).map(img => {
            return new Promise((resolve) => {
              if (img.complete) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true);
              }
            });
          });
          await Promise.all(imagePromises);
          
          // Generate canvas from HTML
          const canvas = await html2canvas(clonedElement, {
            useCORS: true,
            logging: false,
            allowTaint: true,
            imageTimeout: 15000
          } as any);
          
          // Remove cloned element
          document.body.removeChild(clonedElement);
          
          // Generate PDF
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          });
          
          const margin = 15; // 15mm margins
          const imgWidth = 210 - (margin * 2); // A4 width minus margins
          const pageHeight = 297; // A4 height in mm
          const footerHeight = 20; // Height reserved for footer
          const headerHeight = 10; // Height reserved for header on subsequent pages
          const firstPageContentHeight = pageHeight - margin - footerHeight; // First page has more space
          const contentHeight = pageHeight - (margin * 2) - footerHeight - headerHeight; // Subsequent pages
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = margin;
          let pageNumber = 1;
          
          // Function to add header on subsequent pages
          const addHeader = (page: number) => {
            if (page > 1) {
              pdf.setFontSize(8);
              pdf.setTextColor(128, 128, 128);
              pdf.text('OrthoMind - Orthodontic Treatment Plan', margin, margin - 5);
              // Add a thin line
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, margin - 2, 210 - margin, margin - 2);
            }
          };
          
          // Function to add footer
          const addFooter = (page: number) => {
            // Add a thin line above footer
            pdf.setDrawColor(200, 200, 200);
            pdf.line(margin, pageHeight - footerHeight, 210 - margin, pageHeight - footerHeight);
            
            pdf.setFontSize(10);
            pdf.setTextColor(128, 128, 128);
            // Add page number on the left
            pdf.text(`Page ${page}`, margin, pageHeight - 10);
            // Add "Exported with OrthoMind" on the right
            const exportText = 'Exported with OrthoMind';
            const textWidth = pdf.getTextWidth(exportText);
            pdf.text(exportText, 210 - margin - textWidth, pageHeight - 10);
            // Add date in the center
            const dateText = new Date().toLocaleDateString();
            const dateWidth = pdf.getTextWidth(dateText);
            pdf.text(dateText, (210 - dateWidth) / 2, pageHeight - 10);
          };
          
          // Add first page
          pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
          addFooter(pageNumber);
          heightLeft -= firstPageContentHeight;
          
          // Add additional pages if needed
          while (heightLeft >= 0) {
            position = heightLeft - imgHeight + margin + headerHeight;
            pdf.addPage();
            pageNumber++;
            addHeader(pageNumber);
            pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
            addFooter(pageNumber);
            heightLeft -= contentHeight;
          }
          
          pdf.save(`OrthoMind-${documentTitle}.pdf`);
          
          // Track export event
          orthoTrack.treatmentPlanExported('pdf', getCurrentSessionId());
        } catch (error) {
          console.error('Error generating PDF:', error);
          alert('Error generating PDF. Please try again.');
        }
      }
    }
    setShowExportMenu(false);
  }, [editor, documentTitle]);

  const handleExportDOCX = useCallback(() => {
    if (editor) {
      // Create a formatted version for Word
      const content = editor.getHTML();
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${documentTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; }
            h1, h2, h3 { color: #333; }
            ul, ol { margin-left: 20px; }
            strong { font-weight: bold; }
            em { font-style: italic; }
          </style>
        </head>
        <body>
          ${content}
        </body>
        </html>
      `;
      
      const blob = new Blob([htmlContent], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentTitle}.doc`;
      a.click();
      URL.revokeObjectURL(url);
      
      // Track export event
      orthoTrack.treatmentPlanExported('word', getCurrentSessionId());
    }
    setShowExportMenu(false);
  }, [editor, documentTitle]);

  const insertTreatmentPlanTemplate = () => {
    if (editor) {
      const sessionId = getCurrentSessionId();
      const templateContent = `
        <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
        <div style="margin-bottom: 20px;">
          <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p><strong>Case ID:</strong> ${sessionId ? hashSessionId(sessionId) : 'Pending'}</p>
        </div>
        <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
        
        <h2>Patient Information</h2>
        <p><strong>Name:</strong> [Patient Name]</p>
        <p><strong>Age:</strong> [Age]</p>
        <p><strong>Chief Complaint:</strong> [Main concern]</p>
        
        <h2>Clinical Assessment</h2>
        <p>[Enter clinical findings here]</p>
        
        <h2>Treatment Objectives</h2>
        <ul>
          <li>[Objective 1]</li>
          <li>[Objective 2]</li>
          <li>[Objective 3]</li>
        </ul>
        
        <h2>Treatment Plan</h2>
        <p>[Detailed treatment approach]</p>
        
        <h2>Expected Duration</h2>
        <p>[Treatment timeline]</p>
        
        <h2>Notes</h2>
        <p>[Additional notes or considerations]</p>
      `;

      editor.commands.setContent(templateContent);
      setIsDirty(true);
    }
  };

  if (!editor) {
    return <div className="p-4">Loading editor...</div>;
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-medical-200 p-3 sm:p-4">
        {/* Manual Fetch Buttons */}
        <div className="flex items-center justify-center gap-2 mb-3 pb-3 border-b border-medical-100">
          <button
            onClick={handleFetchCriteria}
            disabled={isFetchingCriteria}
            className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Manually fetch case criteria"
          >
            <ClipboardList className={`w-4 h-4 ${isFetchingCriteria ? 'animate-pulse' : ''}`} />
            <span>{isFetchingCriteria ? 'Fetching...' : 'Fetch Case Criteria'}</span>
          </button>
          
          <button
            onClick={handleFetchPlan}
            disabled={isFetchingPlan}
            className="px-3 py-1.5 text-sm bg-green-50 text-green-700 hover:bg-green-100 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Manually fetch treatment plan"
          >
            <FileSearch className={`w-4 h-4 ${isFetchingPlan ? 'animate-pulse' : ''}`} />
            <span>{isFetchingPlan ? 'Fetching...' : 'Fetch Treatment Plan'}</span>
          </button>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-medical-600" />
            <input
              type="text"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              className="text-base sm:text-lg font-medium bg-transparent border-none outline-none focus:ring-0 w-full sm:w-auto"
            />
            {isDirty && <span className="text-xs text-orange-500">• Unsaved</span>}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="medical-button-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base flex items-center space-x-1 sm:space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh document with latest criteria"
            >
              <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>

            <button
              onClick={handleSave}
              className="medical-button-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base flex items-center space-x-1 sm:space-x-2"
            >
              <Save className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Save</span>
            </button>

            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="medical-button-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base flex items-center space-x-1 sm:space-x-2"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Export</span>
                <ChevronDown className="w-2 h-2 sm:w-3 sm:h-3" />
              </button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-medical-200 z-50">
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4 text-red-600" />
                    <span>Export as PDF</span>
                  </button>
                  <button
                    onClick={handleExportDOCX}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span>Export as Word</span>
                  </button>
                  <button
                    onClick={handleExportHTML}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4 text-gray-600" />
                    <span>Export as HTML</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Template Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <span className="text-xs sm:text-sm text-medical-600 mr-1 sm:mr-2">Template:</span>
            <button
              onClick={() => insertTreatmentPlanTemplate()}
              className="medical-button-secondary text-xs px-2 sm:px-3 py-1"
            >
              Treatment Plan Template
            </button>
          </div>
          
          {/* Treatment Plan Selector */}
          {sessionTreatmentPlans.length > 0 && (
            <div className="flex items-center space-x-2">
              <FileDown className="w-3 h-3 sm:w-4 sm:h-4 text-medical-600" />
              <select
                value={currentTreatmentPlan?.id || ''}
                onChange={(e) => {
                  const plan = sessionTreatmentPlans.find(p => p.id === e.target.value);
                  if (plan) setCurrentTreatmentPlan(plan);
                }}
                className="text-xs sm:text-sm border border-medical-300 rounded px-1 sm:px-2 py-1 max-w-[150px] sm:max-w-none truncate"
              >
                <option value="">Select Treatment Plan</option>
                {sessionTreatmentPlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center space-x-1 p-1.5 sm:p-2 bg-medical-50 rounded-lg overflow-x-auto">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`
              p-2 rounded transition-colors
              ${editor.isActive('bold')
                ? 'bg-primary-100 text-primary-700'
                : 'text-medical-600 hover:bg-medical-100'
              }
            `}
          >
            <BoldIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`
              p-2 rounded transition-colors
              ${editor.isActive('italic')
                ? 'bg-primary-100 text-primary-700'
                : 'text-medical-600 hover:bg-medical-100'
              }
            `}
          >
            <ItalicIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>

          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`
              p-2 rounded transition-colors
              ${editor.isActive('underline')
                ? 'bg-primary-100 text-primary-700'
                : 'text-medical-600 hover:bg-medical-100'
              }
            `}
          >
            <UnderlineIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>

          <div className="w-px h-6 bg-medical-300 mx-2" />

          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`
              p-2 rounded transition-colors
              ${editor.isActive('bulletList')
                ? 'bg-primary-100 text-primary-700'
                : 'text-medical-600 hover:bg-medical-100'
              }
            `}
          >
            <List className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>

        <div className="text-xs text-medical-500 mt-2">
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-3 sm:p-6">
          <EditorContent
            editor={editor}
            className="min-h-[300px] sm:min-h-[500px] prose prose-sm sm:prose prose-medical max-w-none"
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;
